/* ══════════════════════════════════════════════════════════════
   Hash-Based Client-Side Router
   Supports 8 pages with page transitions
   ══════════════════════════════════════════════════════════════ */

import { getRouteByPath } from '../config/routes';

type PageRenderer = () => string;
type PageInitFn = () => void;
type RouteChangeCallback = (path: string, name: string) => void;

const pageRenderers = new Map<string, PageRenderer>();
const pageInitFns = new Map<string, PageInitFn>();
const callbacks: RouteChangeCallback[] = [];
const LEGACY_ROUTE_REDIRECTS: Record<string, string> = {
    '/map': '/world-monitor',
};

function resolvePath(path: string): string {
    return LEGACY_ROUTE_REDIRECTS[path] || path;
}

/**
 * Register a page renderer for a route, with optional post-render init
 */
export function registerPage(path: string, renderer: PageRenderer, initFn?: PageInitFn): void {
    pageRenderers.set(path, renderer);
    if (initFn) pageInitFns.set(path, initFn);
}

/**
 * Subscribe to route changes
 */
export function onRouteChange(callback: RouteChangeCallback): () => void {
    callbacks.push(callback);
    return () => {
        const idx = callbacks.indexOf(callback);
        if (idx !== -1) callbacks.splice(idx, 1);
    };
}

/**
 * Navigate to a route
 */
export function navigate(path: string): void {
    window.location.hash = resolvePath(path);
}

/**
 * Get current route path from hash
 */
export function getCurrentPath(): string {
    const hash = window.location.hash.slice(1) || '/';
    return resolvePath(hash);
}

/**
 * Render the current page into a container
 */
export function renderCurrentPage(): string {
    const path = getCurrentPath();
    const renderer = pageRenderers.get(path);

    if (renderer) {
        return renderer();
    }

    // 404 fallback
    return `
    <div class="page-404 animate-fade-in-up" style="text-align: center; padding: var(--space-16);">
      <h1 style="font-size: var(--text-4xl); margin-bottom: var(--space-4);">404</h1>
      <p style="color: var(--text-secondary); margin-bottom: var(--space-6);">Page not found</p>
      <button class="glass-btn glass-btn--primary" onclick="window.location.hash='/'">
        🏠 Go to Dashboard
      </button>
    </div>
  `;
}

/**
 * Initialize the router — listen for hash changes
 */
export function initRouter(): void {
    const handleRouteChange = () => {
        const rawPath = window.location.hash.slice(1) || '/';
        const path = resolvePath(rawPath);
        if (path !== rawPath) {
            window.location.hash = path;
            return;
        }

        const route = getRouteByPath(path);
        const name = route?.name || 'unknown';

        // Notify subscribers
        callbacks.forEach(cb => cb(path, name));

        // Render page
        const content = document.getElementById('page-content');
        if (content) {
            content.classList.remove('page-enter');
            // Force reflow
            void content.offsetHeight;
            content.innerHTML = renderCurrentPage();
            content.classList.add('page-enter');

            // Call page init function (mounts components into placeholders)
            const initFn = pageInitFns.get(path);
            if (initFn) initFn();
        }

        // Update active nav links
        document.querySelectorAll('.top-nav__link, .bottom-nav__link, .terminal-nav__link').forEach(link => {
            const href = link.getAttribute('data-path');
            if (href === path) {
                link.classList.add('top-nav__link--active', 'bottom-nav__link--active', 'terminal-nav__link--active');
            } else {
                link.classList.remove('top-nav__link--active', 'bottom-nav__link--active', 'terminal-nav__link--active');
            }
        });

        // Update document title
        if (route) {
            document.title = `${route.label} — India Market Monitor`;
        }
    };

    window.addEventListener('hashchange', handleRouteChange);

    // Handle initial load
    if (!window.location.hash) {
        window.location.hash = '/';
    }
    handleRouteChange();
}

/**
 * Get all registered routes for debugging
 */
export function getRegisteredRoutes(): string[] {
    return Array.from(pageRenderers.keys());
}
